import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Upload, AlertCircle, FolderPlus, ArrowRight, Sparkles } from 'lucide-react';
import { Button, Input, Select, Label, ErrorMessage } from '../styles/GlobalStyles';
import { theme } from '../styles/theme';
import { FormData as TenderFormData, ResponseMode } from '../types';
import ResponseModeSelector from './ResponseModeSelector';
import SuccessfulTenderUploader from './SuccessfulTenderUploader';
import { ProjectApi, ProjectData } from '../services/projectApi';
import { useAuth } from '../context/AuthContext';
import { TenderMode } from './SuccessfulTenderModeSelector';
import { TenderDocumentData } from '../services/tenderDocumentApi';
import { uploadService } from '../services/uploadService';
import { KnowledgeBaseApi } from '../services/knowledgeBaseApi';
import EmptyKnowledgeBaseModal, { MIN_DOCS_RECOMMENDED } from './EmptyKnowledgeBaseModal';

const FormContainer = styled.div`
  max-width: 100%;
`;

const FormField = styled.div`
  margin-bottom: ${theme.spacing[6]};
`;

const MultiInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
`;

const MultiInputRow = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
  align-items: flex-end;
`;

const MultiInputField = styled.div`
  flex: 1;
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.surface};
  color: ${theme.colors.textSecondary};
  cursor: pointer;
  transition: all ${theme.transitions.base};

  &:hover {
    border-color: ${theme.colors.primary[500]};
    color: ${theme.colors.primary[600]};
    background: ${theme.colors.primary[50]};
  }

  &.danger {
    &:hover {
      border-color: ${theme.colors.error[500]};
      color: ${theme.colors.error[600]};
      background: ${theme.colors.error[100]};
    }
  }
`;

const HelpText = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textMuted};
  margin-top: ${theme.spacing[1]};
`;

const InfoBox = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[4]};
  background: ${theme.colors.info[100]};
  border: 1px solid ${theme.colors.info[500]}20;
  border-radius: ${theme.borderRadius.md};
  margin-bottom: ${theme.spacing[6]};
`;

const InfoContent = styled.div`
  flex: 1;
`;

const InfoTitle = styled.h4`
  color: ${theme.colors.info[700]};
  margin-bottom: ${theme.spacing[1]};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-size: ${theme.typography.fontSize.sm};
`;

const InfoText = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textSecondary};
  line-height: ${theme.typography.lineHeight.relaxed};
`;

const NoProjectsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${theme.spacing[8]} ${theme.spacing[6]};
  text-align: center;
  background: linear-gradient(135deg, ${theme.colors.primary[50]}, ${theme.colors.accent[50]});
  border: 2px dashed ${theme.colors.primary[200]};
  border-radius: ${theme.borderRadius.xl};
  margin: ${theme.spacing[2]} 0;
  position: relative;
  overflow: hidden;
  transition: all ${theme.transitions.base};
  
  &:hover {
    border-color: ${theme.colors.primary[300]};
    background: linear-gradient(135deg, ${theme.colors.primary[100]}, ${theme.colors.accent[100]});
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.lg};
  }
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(
      circle at center,
      ${theme.colors.primary[100]}20 0%,
      transparent 50%
    );
    animation: float 6s ease-in-out infinite;
    pointer-events: none;
  }
  
  @keyframes float {
    0%, 100% { transform: translate(0, 0) rotate(0deg); }
    33% { transform: translate(10px, -10px) rotate(1deg); }
    66% { transform: translate(-5px, 5px) rotate(-1deg); }
  }
`;

const NoProjectsIcon = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  border-radius: ${theme.borderRadius.full};
  background: linear-gradient(135deg, ${theme.colors.primary[500]}, ${theme.colors.accent[600]});
  color: white;
  margin-bottom: ${theme.spacing[4]};
  box-shadow: ${theme.shadows.lg};
  
  &::after {
    content: '';
    position: absolute;
    top: -4px;
    left: -4px;
    right: -4px;
    bottom: -4px;
    border-radius: ${theme.borderRadius.full};
    background: linear-gradient(45deg, ${theme.colors.primary[300]}, ${theme.colors.accent[300]});
    z-index: -1;
    animation: pulse-border 2s ease-in-out infinite;
  }
  
  @keyframes pulse-border {
    0%, 100% { opacity: 0.7; transform: scale(1); }
    50% { opacity: 1; transform: scale(1.05); }
  }
`;

const NoProjectsTitle = styled.h3`
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.textPrimary};
  margin-bottom: ${theme.spacing[3]};
  font-family: ${theme.typography.fontFamily.display};
  position: relative;
  z-index: 1;
`;

const NoProjectsDescription = styled.p`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.textSecondary};
  line-height: ${theme.typography.lineHeight.relaxed};
  margin-bottom: ${theme.spacing[6]};
  max-width: 400px;
  position: relative;
  z-index: 1;
`;

const CreateProjectButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  background: linear-gradient(135deg, ${theme.colors.primary[500]}, ${theme.colors.accent[600]});
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  box-shadow: ${theme.shadows.md};
  position: relative;
  z-index: 1;
  
  &:hover {
    background: linear-gradient(135deg, ${theme.colors.primary[600]}, ${theme.colors.accent[700]});
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.xl};
  }
  
  &:active {
    transform: translateY(0);
  }
  
  svg {
    transition: transform ${theme.transitions.base};
  }
  
  &:hover svg:last-child {
    transform: translateX(2px);
  }
`;

interface URLUploadTabProps {
  onSubmit: (data: TenderFormData) => void;
  isLoading?: boolean;
  initialData?: Partial<TenderFormData>;
}

const URLUploadTab: React.FC<URLUploadTabProps> = ({ 
  onSubmit, 
  isLoading = false, 
  initialData 
}) => {
  const { state: authState } = useAuth();
  const [formData, setFormData] = useState<TenderFormData>({
    url: initialData?.url || '',
    successful_tender_urls: initialData?.successful_tender_urls || [],
    company_id: initialData?.company_id || authState.user?.id || '318',
    project_id: initialData?.project_id || '',
  });

  const [responseMode, setResponseMode] = useState<ResponseMode>(initialData?.response_mode || 'generate');
  const [successfulTenderMode, setSuccessfulTenderMode] = useState<TenderMode>('select');
  const [selectedBankDocuments, setSelectedBankDocuments] = useState<TenderDocumentData[]>([]);

  const [errors, setErrors] = useState<Partial<Record<keyof TenderFormData | 'projects', string>>>({});
  const [projects, setProjects] = useState<ProjectData[]>([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);

  // P2.4 — KB pre-flight gating. We pre-fetch stats once on mount so the
  // modal can decide whether to fire before the user even hits Submit. Skips
  // the gate entirely on response_mode='refine' since refine doesn't draft.
  const [kbStats, setKbStats] = useState<{
    total: number;
    categoriesFilled: number;
    missingCritical: string[];
  } | null>(null);
  const [showEmptyKbModal, setShowEmptyKbModal] = useState(false);

  // Fetch projects on component mount
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setIsLoadingProjects(true);
        const response = await ProjectApi.getProjects(1, 100); // Get first 100 projects
        
        if (response.success && response.data) {
          setProjects(response.data.projects);
        } else {
          console.error('Failed to fetch projects:', response.error);
          setErrors(prev => ({ ...prev, projects: response.error || 'Failed to load projects' }));
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
        setErrors(prev => ({ ...prev, projects: 'Failed to load projects' }));
      } finally {
        setIsLoadingProjects(false);
      }
    };

    fetchProjects();
  }, []);

  // P2.4 — fetch KB stats + setup progress once. Failures are non-fatal:
  // we don't want a flaky stats endpoint to block legitimate analyses, so
  // we silently skip the gate when the data isn't available.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [statsRes, progressRes] = await Promise.all([
          KnowledgeBaseApi.getStats(),
          KnowledgeBaseApi.getSetupProgress(),
        ]);
        if (cancelled) return;
        const total = statsRes.success && statsRes.data ? statsRes.data.total_documents : 0;
        const categoriesFilled =
          statsRes.success && statsRes.data
            ? (statsRes.data.categories_with_documents || []).length
            : 0;
        const missingCritical =
          progressRes.success && progressRes.data
            ? progressRes.data.missing_critical_categories || []
            : [];
        setKbStats({ total, categoriesFilled, missingCritical });
      } catch (err) {
        // Silent — fall through to no-gate behaviour.
        console.warn('KB pre-flight stats unavailable:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const uploadFile = async (file: File): Promise<string> => {
    try {
      const url = await uploadService.uploadFile(file, {
        timeout: 30000
      });
      
      return url;
    } catch (error) {
      console.error('Upload error:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to upload file');
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof TenderFormData, string>> = {};

    // Validate URL
    if (!formData.url.trim()) {
      newErrors.url = 'Tender document URL is required';
    } else if (!isValidUrl(formData.url)) {
      newErrors.url = 'Please enter a valid URL';
    }

    // Validate company ID
    if (!formData.company_id.trim()) {
      newErrors.company_id = 'Company ID is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (string: string): boolean => {
    try {
      const url = new URL(string);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch (_) {
      return false;
    }
  };

  const processSubmission = async () => {
    try {
      // Get successful tender URLs from selected bank documents
      const successfulUrls: string[] = [];

      if (successfulTenderMode === 'select' && selectedBankDocuments.length > 0) {
        // Use URLs from selected tender bank documents
        successfulUrls.push(...selectedBankDocuments.map(doc => doc.url).filter((url): url is string => Boolean(url)));
      }
      // If mode is 'none', successfulUrls remains empty

      const submitData: TenderFormData = {
        url: formData.url,
        company_id: formData.company_id,
        project_id: formData.project_id,
        successful_tender_urls: successfulUrls,
        response_mode: responseMode,
      };

      onSubmit(submitData);
    } catch (error) {
      console.error('Submit error:', error);
      setErrors(prev => ({
        ...prev,
        successful_tender_urls: error instanceof Error ? error.message : 'Submission failed. Please try again.'
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    // P2.4 — KB pre-flight gate. Only fires when:
    //   • response_mode is 'generate' (drafts will be authored)
    //   • we successfully loaded stats (kbStats !== null)
    //   • the KB is under the recommended threshold
    if (
      responseMode === 'generate' &&
      kbStats !== null &&
      kbStats.total < MIN_DOCS_RECOMMENDED
    ) {
      setShowEmptyKbModal(true);
      return;
    }

    processSubmission();
  };

  const handleProceedAnyway = () => {
    setShowEmptyKbModal(false);
    processSubmission();
  };

  return (
    <FormContainer>
      <InfoBox>
        <AlertCircle size={20} color={theme.colors.info[500]} />
        <InfoContent>
          <InfoTitle>URL Upload</InfoTitle>
          <InfoText>
            Enter the URL of your tender document to start AI-powered analysis. Our system supports 
            PDF documents from most public URLs and cloud storage sharing links.
          </InfoText>
        </InfoContent>
      </InfoBox>

      <form onSubmit={handleSubmit}>
        <FormField>
          <Label htmlFor="url">
            Tender Document URL *
          </Label>
          <Input
            id="url"
            type="url"
            placeholder="https://tender.com/tender-document.pdf"
            value={formData.url}
            onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
            $hasError={!!errors.url}
            disabled={isLoading}
          />
          {errors.url && <ErrorMessage>{errors.url}</ErrorMessage>}
          <HelpText>
            Enter the URL of your tender document (PDF format supported)
          </HelpText>
        </FormField>

        <FormField>
          <Label>
            Company ID *
          </Label>
          <Input
            type="text"
            placeholder="318"
            value={formData.company_id}
            onChange={(e) => setFormData(prev => ({ ...prev, company_id: e.target.value }))}
            $hasError={!!errors.company_id}
            disabled={true}
            style={{ 
              backgroundColor: theme.colors.backgroundSecondary, 
              cursor: 'not-allowed',
              opacity: 0.7 
            }}
          />
          {errors.company_id && <ErrorMessage>{errors.company_id}</ErrorMessage>}
          <HelpText>
            Company identifier set to 318 (auto-assigned for demo purposes)
          </HelpText>
        </FormField>

        <FormField>
          <Label>
            Select Project (Optional)
          </Label>
          <Select
            value={formData.project_id}
            onChange={(e) => setFormData(prev => ({ ...prev, project_id: e.target.value }))}
            disabled={isLoading || isLoadingProjects}
          >
            <option value="">
              {isLoadingProjects ? '-- Loading projects... --' : '-- Select an existing project --'}
            </option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </Select>
          {errors.project_id && <ErrorMessage>{errors.project_id}</ErrorMessage>}
          {errors.projects && <ErrorMessage>{errors.projects}</ErrorMessage>}
          {projects.length === 0 && !isLoadingProjects ? (
            <NoProjectsContainer>
              <NoProjectsIcon>
                <FolderPlus size={32} />
              </NoProjectsIcon>
              <NoProjectsTitle>No Projects Found</NoProjectsTitle>
              <NoProjectsDescription>
                Start your tender analysis journey by creating your first project. 
                Projects help organize and manage your tender documents effectively.
              </NoProjectsDescription>
              <Link to="/projects" style={{ textDecoration: 'none' }}>
                <CreateProjectButton>
                  <Sparkles size={16} />
                  Create Your First Project
                  <ArrowRight size={16} />
                </CreateProjectButton>
              </Link>
            </NoProjectsContainer>
          ) : (
            <HelpText>
              Associate this analysis with an existing project or leave blank to create a new one
            </HelpText>
          )}
        </FormField>

        <FormField>
          <Label>
            Response Mode
          </Label>
          <ResponseModeSelector
            mode={responseMode}
            onChange={setResponseMode}
            disabled={isLoading}
          />
          <HelpText>
            Choose how the AI should process your tender document
          </HelpText>
        </FormField>

        <FormField>
          <Label>
            Tender Bank
          </Label>
          <SuccessfulTenderUploader
            disabled={isLoading}
            mode={successfulTenderMode}
            onModeChange={setSuccessfulTenderMode}
            selectedBankDocuments={selectedBankDocuments}
            onBankDocumentsChange={setSelectedBankDocuments}
            showModeSelector={false}
          />
          {errors.successful_tender_urls && <ErrorMessage>{errors.successful_tender_urls}</ErrorMessage>}
        </FormField>

        <Button
          type="submit"
          $size="lg"
          $fullWidth
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <div className="pulse">Processing...</div>
            </>
          ) : (
            <>
              <Upload size={20} />
              Start Analysis
            </>
          )}
        </Button>
      </form>

      {kbStats && (
        <EmptyKnowledgeBaseModal
          open={showEmptyKbModal}
          documentCount={kbStats.total}
          categoriesWithDocuments={kbStats.categoriesFilled}
          missingCriticalCategories={kbStats.missingCritical}
          onProceedAnyway={handleProceedAnyway}
          onCancel={() => setShowEmptyKbModal(false)}
        />
      )}
    </FormContainer>
  );
};

export default URLUploadTab;